import React, { useState } from 'react';
import type { Tile } from '../../types/mjai';
import { getTileLabel, isRedFive } from '../../utils/tileHelpers';

const CDN = 'https://cdn.jsdelivr.net/gh/FluffyStuff/riichi-mahjong-tiles@master/Regular';

const TILE_FILE: Record<string, string> = {
  // 萬子
  '1m': 'Man1', '2m': 'Man2', '3m': 'Man3', '4m': 'Man4',
  '5m': 'Man5', '6m': 'Man6', '7m': 'Man7', '8m': 'Man8', '9m': 'Man9',
  '0m': 'Man5-Dora',
  // 筒子
  '1p': 'Pin1', '2p': 'Pin2', '3p': 'Pin3', '4p': 'Pin4',
  '5p': 'Pin5', '6p': 'Pin6', '7p': 'Pin7', '8p': 'Pin8', '9p': 'Pin9',
  '0p': 'Pin5-Dora',
  // 索子
  '1s': 'Sou1', '2s': 'Sou2', '3s': 'Sou3', '4s': 'Sou4',
  '5s': 'Sou5', '6s': 'Sou6', '7s': 'Sou7', '8s': 'Sou8', '9s': 'Sou9',
  '0s': 'Sou5-Dora',
  // 字牌
  '1z': 'Ton', '2z': 'Nan', '3z': 'Shaa', '4z': 'Pei',
  '5z': 'Haku', '6z': 'Hatsu', '7z': 'Chun',
};

function getTileImageUrl(tile: Tile): string | null {
  const file = TILE_FILE[tile];
  return file ? `${CDN}/${file}.svg` : null;
}

interface TileImageProps {
  tile: Tile;
  size?: 'sm' | 'md' | 'lg';
  selected?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
}

const SIZE_CLASSES = {
  sm: 'w-8 h-11',
  md: 'w-10 h-14',
  lg: 'w-14 h-20',
};

export default function TileImage({
  tile,
  size = 'md',
  selected = false,
  onClick,
  disabled = false,
  className = '',
}: TileImageProps) {
  const [imgError, setImgError] = useState(false);
  const imageUrl = getTileImageUrl(tile);
  const label = getTileLabel(tile);
  const red = isRedFive(tile);

  const baseClass = [
    'inline-flex items-center justify-center rounded select-none relative overflow-hidden bg-white border border-black',
    SIZE_CLASSES[size],
    selected ? 'ring-2 ring-yellow-400 -translate-y-1.5 shadow-md' : '',
    onClick && !disabled ? 'cursor-pointer hover:-translate-y-1 transition-transform hover:shadow' : '',
    disabled ? 'opacity-40 cursor-not-allowed' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div
      className={baseClass}
      onClick={!disabled ? onClick : undefined}
      title={label}
      role={onClick ? 'button' : undefined}
      aria-pressed={onClick ? selected : undefined}
    >
      {imageUrl && !imgError ? (
        <img
          src={imageUrl}
          alt={label}
          className="w-full h-full object-contain"
          onError={() => setImgError(true)}
          draggable={false}
        />
      ) : (
        // フォールバック: テキスト表示
        <div className={`
          w-full h-full flex items-center justify-center text-center
          border-2 rounded bg-white
          ${red ? 'border-red-400 ring-1 ring-red-300' : 'border-gray-400'}
          ${selected ? 'bg-yellow-50' : ''}
        `}>
          <span className="text-xs font-bold leading-tight px-0.5 break-all">{label}</span>
        </div>
      )}
    </div>
  );
}
